import axios from 'axios';

/**
 * Match Executive Chat / RentalDashboard: API calls expect a base that ends with `/api`.
 * Production sometimes sets `REACT_APP_API_URL` to the API host only (no `/api`), which
 * would request `/microdeb-dashboard/...` on the wrong path and return 404.
 */
function getMicrodebApiBaseUrl(): string {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null || raw.trim() === '') {
    return '/api';
  }
  let base = raw.trim().replace(/\/+$/, '');
  if (/\/api$/i.test(base)) {
    return base;
  }
  if (/^https?:\/\//i.test(base)) {
    return `${base}/api`;
  }
  return base.startsWith('/') ? base : `/${base}`;
}

const API_BASE_URL = getMicrodebApiBaseUrl();

export interface MicrodebRevenueMonth {
  month: string;
  revenue: number;
}

export interface MicrodebRevenueDashboardData {
  totalRevenue: number;
  latestMonthRevenue: number;
  averageMonthlyRevenue: number;
  momChangePercent: number | null;
  monthlyRevenue: MicrodebRevenueMonth[];
  currency?: string;
}

type MicrodebRevenueApiPayload = {
  success?: boolean;
  data?: unknown;
  [key: string]: unknown;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getField = (source: Record<string, unknown>, fields: string[]): unknown => {
  for (const field of fields) {
    if (source[field] !== undefined && source[field] !== null) return source[field];
  }
  return undefined;
};

const normalizeMonth = (item: unknown): MicrodebRevenueMonth | null => {
  if (!item || typeof item !== 'object') return null;

  const source = item as Record<string, unknown>;
  const rawLabel = getField(source, ['label', 'period', 'monthLabel']);
  const rawYear = getField(source, ['year', 'Year']);
  const rawMonthNumber = getField(source, ['month', 'Month', 'monthNumber']);
  const rawMonth = rawLabel || (
    rawYear !== undefined && rawMonthNumber !== undefined
      ? `${rawYear}-${String(rawMonthNumber).padStart(2, '0')}`
      : rawMonthNumber
  );
  const rawRevenue = getField(source, ['revenue', 'totalRevenue', 'total_revenue', 'value']);

  if (!rawMonth) return null;

  return {
    month: String(rawMonth),
    revenue: toNumber(rawRevenue)
  };
};

const normalizeRevenueData = (payload: MicrodebRevenueApiPayload): MicrodebRevenueDashboardData => {
  const rawData = (payload.data && typeof payload.data === 'object')
    ? payload.data as Record<string, unknown>
    : payload as Record<string, unknown>;
  const rawSummary = (rawData.summary && typeof rawData.summary === 'object')
    ? rawData.summary as Record<string, unknown>
    : {};

  const rawMonthlyRevenue = getField(rawData, [
    'monthlyRevenue',
    'monthly_revenue',
    'months',
    'trend',
    'revenueTrend',
    'revenue_trend'
  ]);

  const monthlyRevenue = Array.isArray(rawMonthlyRevenue)
    ? rawMonthlyRevenue.map(normalizeMonth).filter((month): month is MicrodebRevenueMonth => month !== null)
    : [];

  const totalRevenue = toNumber(getField(rawData, ['totalRevenue', 'total_revenue'])) ||
    toNumber(getField(rawSummary, ['totalRevenue', 'total_revenue'])) ||
    monthlyRevenue.reduce((sum, month) => sum + month.revenue, 0);

  const latestMonthRevenue = toNumber(getField(rawData, ['latestMonthRevenue', 'latest_month_revenue'])) ||
    toNumber(getField(rawSummary, ['latestMonthRevenue', 'latest_month_revenue'])) ||
    (monthlyRevenue.length > 0 ? monthlyRevenue[monthlyRevenue.length - 1].revenue : 0);

  const averageMonthlyRevenue = toNumber(getField(rawData, ['averageMonthlyRevenue', 'average_monthly_revenue', 'avgMonthlyRevenue', 'avg_monthly_revenue'])) ||
    toNumber(getField(rawSummary, ['averageMonthlyRevenue', 'average_monthly_revenue', 'avgMonthlyRevenue', 'avg_monthly_revenue'])) ||
    (monthlyRevenue.length > 0 ? totalRevenue / monthlyRevenue.length : 0);

  const rawMomChange = getField(rawData, ['momChangePercent', 'mom_change_percent', 'monthOverMonthChange', 'month_over_month_change']) ??
    getField(rawSummary, ['momChangePercent', 'mom_change_percent', 'monthOverMonthChangePct', 'month_over_month_change_pct']);
  const fallbackMomChange = monthlyRevenue.length >= 2 && monthlyRevenue[monthlyRevenue.length - 2].revenue !== 0
    ? ((monthlyRevenue[monthlyRevenue.length - 1].revenue - monthlyRevenue[monthlyRevenue.length - 2].revenue) / monthlyRevenue[monthlyRevenue.length - 2].revenue) * 100
    : null;

  return {
    totalRevenue,
    latestMonthRevenue,
    averageMonthlyRevenue,
    momChangePercent: rawMomChange === undefined || rawMomChange === null ? fallbackMomChange : toNumber(rawMomChange),
    monthlyRevenue,
    currency: typeof rawData.currency === 'string' ? rawData.currency : undefined
  };
};

class MicrodebDashboardService {
  private revenueDashboardCache: MicrodebRevenueDashboardData | null = null;
  private revenueDashboardRequest: Promise<MicrodebRevenueDashboardData> | null = null;

  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getRevenueDashboard(): Promise<MicrodebRevenueDashboardData> {
    if (this.revenueDashboardCache) {
      return this.revenueDashboardCache;
    }

    if (!this.revenueDashboardRequest) {
      this.revenueDashboardRequest = axios.get<MicrodebRevenueApiPayload>(
        `${API_BASE_URL}/microdeb-dashboard/revenue`,
        { headers: this.getAuthHeaders() }
      ).then(response => {
        if (response.data.success === false) {
          throw new Error(String(response.data.error || 'Failed to load Microdeb revenue dashboard'));
        }

        const dashboardData = normalizeRevenueData(response.data);
        this.revenueDashboardCache = dashboardData;
        return dashboardData;
      }).finally(() => {
        this.revenueDashboardRequest = null;
      });
    }

    return this.revenueDashboardRequest;
  }
}

const microdebDashboardService = new MicrodebDashboardService();

export default microdebDashboardService;
